/**
 * RFC 8785 (JCS) canonical JSON.
 *
 * Framework-free and dependency-free on purpose: the browser must be able to
 * re-canonicalise a definition to verify a hash, not just the server.
 *
 * Scalars delegate to JSON.stringify deliberately — RFC 8785 defines number and
 * string serialisation as exactly ECMAScript's, so re-implementing them could only
 * introduce divergence. What is added here is key sorting, rejection of values
 * JSON.stringify would silently corrupt, and stable handling of `undefined`.
 */
export class CanonicalizationError extends Error {
}
const MAX_DEPTH = 64;
export function canonicalize(value) {
    return write(value, 0);
}
function write(value, depth) {
    if (depth > MAX_DEPTH)
        throw new CanonicalizationError('nesting depth exceeded');
    if (value === null)
        return 'null';
    switch (typeof value) {
        case 'boolean':
            return value ? 'true' : 'false';
        case 'number':
            return writeNumber(value);
        case 'string':
            // ES2019 well-formed JSON.stringify escapes lone surrogates. Without that,
            // Buffer/TextEncoder would fold every distinct lone surrogate to U+FFFD and
            // two different strings would hash identically.
            return JSON.stringify(value);
        case 'object':
            break;
        default:
            throw new CanonicalizationError(`non-JSON value of type ${typeof value}`);
    }
    if (Array.isArray(value)) {
        // Array order is significant and is never sorted: reordering a form's fields is
        // a material change and must move the hash.
        return `[${value.map((item) => write(item ?? null, depth + 1)).join(',')}]`;
    }
    assertPlainObject(value);
    // Default Array#sort compares UTF-16 code units, which is exactly what RFC 8785
    // §3.2.3 requires. localeCompare or Intl.Collator here would be a locale- and
    // ICU-version-dependent bug that differs between a laptop and a CI container.
    const keys = Object.keys(value).sort();
    const parts = [];
    for (const key of keys) {
        const child = value[key];
        // Dropped rather than rejected: Cosmos drops undefined on write, so `{a: undefined}`
        // and `{}` must hash the same or a hash computed before the write fails to verify
        // after the read.
        if (child === undefined)
            continue;
        parts.push(`${JSON.stringify(key)}:${write(child, depth + 1)}`);
    }
    return `{${parts.join(',')}}`;
}
function writeNumber(n) {
    if (!Number.isFinite(n)) {
        // JSON.stringify turns these into `null`, collapsing NaN, Infinity and -Infinity
        // into one hash.
        throw new CanonicalizationError(`non-finite number: ${String(n)}`);
    }
    if (Number.isInteger(n) && !Number.isSafeInteger(n)) {
        // 9007199254740993 serialises as ...992. Integer ids above 2^53 must be strings.
        throw new CanonicalizationError(`integer exceeds Number.MAX_SAFE_INTEGER: ${String(n)}`);
    }
    if (Object.is(n, -0))
        return '0';
    return JSON.stringify(n);
}
function assertPlainObject(value) {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
        // Catches Date, Map, Set and class instances. A Date here means someone skipped
        // z.string().datetime(), and toJSON would hide the mistake.
        throw new CanonicalizationError('only plain objects may be canonicalised');
    }
    if ('toJSON' in value) {
        throw new CanonicalizationError('toJSON hook would bypass canonicalisation');
    }
}
/**
 * Normalise controlled text so text that looks identical is identical bytes.
 *
 * This runs on INGEST, inside the Zod schema, so the value that is stored is already
 * normalised. Normalising only at hash time is the classic latent bug: the stored text
 * then no longer re-hashes to its own recorded digest, and re-verifying a signed
 * acknowledgment fails years later with nobody able to reconstruct why.
 */
export function normalizeControlledText(input) {
    return input
        .normalize('NFC') // macOS pastes decomposed, Windows composed
        .replace(/^\uFEFF/, '') // BOM from a .txt paste
        .replace(/\r\n?/g, '\n') // HTML form submission sends CRLF
        .replace(/[\u00A0\u2007\u202F]/g, ' ') // non-breaking spaces from Word
        .replace(/[\u200B-\u200D\u2060\u2028\u2029]/g, '') // zero-width and separators
        .replace(/[ \t]+$/gm, '')
        .trim();
}
//# sourceMappingURL=canonical.js.map